import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useConnectWallet, useSetChain } from '@web3-onboard/react'
import {
  clearState,
  setCryptoPaymentDetails,
  setSpendAllowance,
  setWalletAndChain,
  setWalletError,
  setWalletStatus,
} from '@/store/crypto-wallet'
import * as payState from './paymentState'
import { findTokensBySymbols } from '../../crypto'
import { ethers } from 'ethers'
import { SpendChecker } from './SpendChecker'
import { PriceConverter } from './PriceConverter'
import { getCosting } from '../../utils/InvoiceHelper'
import erc20ABI from './abi/erc20ABI.json'
import common from '@/utils/common'
import keys from '../../config/keys'
import { merge } from 'lodash'

/**
 *
 * useCrypto hook is used along with CryptoPaymentModal to walk the user through setting up
 * their crypto payment. The crypto wallet state drives what is displayed and what step
 * the process is in. This is set by dispatching to the setWalletStatus action.
 */

/**
 * Check whether the guest has sufficient spend allowance setup for Uniswap's Permit2 contract
 */
const checkSpend = async (dispatch, wallet, token, amount) => {
  dispatch(setWalletStatus(payState.SPEND_CHECK))
  const amt = await SpendChecker.checkSpend(token, wallet)
  dispatch(setSpendAllowance(amt))
  if (amt < amount) {
    dispatch(setWalletStatus(payState.SPEND_INSUFFICIENT))
  } else {
    dispatch(setWalletStatus(payState.SPEND_SUFFICIENT))
  }
}

/**
 * Check whether the user's balance is sufficient to cover booking.
 */
const checkBalance = async (props) => {
  const { wallet, token } = props

  const con = new ethers.Contract(
    token.address,
    erc20ABI,
    wallet.ethersProvider,
  )
  const bal = await con.balanceOf(wallet.address)
  return BigInt(bal.toString())
}

/**
 * This increases the guest's spend allowance to the Permit2 contract.
 */
const applySpend = async (dispatch, wallet, token, amount) => {
  dispatch(setWalletStatus(payState.SPEND_INCREASE))
  await SpendChecker.increaseSpend(token, wallet, amount)
  dispatch(setSpendAllowance(amount))
  //note that we're assuming the txn went through to increase spend.
  //more rigor may be needed here to wait a bit and recheck on chain.
  //the painful part is the wait for the blockchain network to sync state
  //with all nodes so that any subsequent lookup gets the most updated state info.
  dispatch(setWalletStatus(payState.SPEND_SUFFICIENT))
}

/**
 * This prompts the user to sign a spend approval for Tripsha hot wallet.
 *
 * IMPORTANT NOTE: the nonce generated is based on what payments have been submitted on-chain, not
 * how many payments are pending. It is possible that if someone makes two payments before being
 * accepted, this nonce value will be reused. At that point, the payment will fail on-chain and
 * the guest will have to resubmit their payment with a new nonce value. A refactor of the
 * backend wallet logic would have to be implemented to 'reserve' and 'use' nonce values.
 */
const getSignature = async (props) => {
  const { wallet, token, amount, expiration } = props
  const { sig, nonce, token: sToken,
    amount: sAmount,
    expiration: sExpiration } = await SpendChecker.getSignature({
    token,
    wallet,
    amount,
    expiration,
  })
  return { sig, nonce, amount: sAmount, token: sToken, expiration: sExpiration }
}

/**
 * Once signature and details are captured, this function updates the backend database
 * through the normal booking flow. See checkout in Views/CheckoutPage along with Views/index
 */
const updatePaymentRecord = async (dispatch, walletState, props) => {
  dispatch(setWalletStatus(payState.UPDATING_PAY_RECORD))
  const { nonce, amount, signature, token, deadline } = props

  //these are the details stored in the backend DB as 'cryptoPaymentMethod' on booking
  const details = {
    nonce: nonce.toString(),
    amount: amount.toString(),
    token: token.address,
    chainId: token.chainId,
    wallet: walletState.wallet.address,
    signature,
    deadline,
  }
  dispatch(setCryptoPaymentDetails(details))
}

/**
 * Main hook to use crypto payment as an option. See views/CheckoutPage/checkout for usage.
 */
export const useCryptoPay = (organization) => {
  const cryptoWallet = useSelector((state) => state.cryptoWallet)
  const { confirmedTrips } = useSelector((state) => state.dashboard)
  const { wallet, status } = cryptoWallet
  const dispatch = useDispatch()

  //local state while we walk guest through process

  //spend allowance signature
  const [sig, setSig] = useState(null)

  //unique nonce included as part of signature (retrieved from backend)
  const [nonce, setNonce] = useState(null)

  //networks that the host has approved
  const [allowedNetworks, setAllowedNetworks] = useState([])

  //tokens the host has approved
  const [allowedTokens, setAllowedTokens] = useState([])

  //the network the guest picked
  const [selectedNetwork, setSelectedNetwork] = useState(0)

  //the network the user has selected in their wallet
  const [walletNetwork, setWalletNetwork] = useState(0)

  //the token the guest picked
  const [selectedToken, setSelectedToken] = useState(null)

  //the USD value for payment
  const [usd, setUSD] = useState(0)

  //the expiration for the spend allowance (i.e. how long the host has to accept registration)
  const [expiry, setExpiry] = useState(0)

  //the converted token units based on current price and USD value (obtained from backend)
  const [tokenUnits, setTokenUnits] = useState(BigInt(0))

  const [signedData, setSignedData] = useState(null);

  const clearLocal = () => {
    setSig(null)
    setNonce(null)
    setSelectedNetwork(0)
    setSelectedToken(null)
    setUSD(0)
    setExpiry(0)
    setTokenUnits(null)
    setSignedData(null)
  }

  //when payment options for booking change
  useEffect(() => {
    if (organization?.cryptoPaymentOptions) {
      //grab the allowed network
      //can't set allowed tokens until guest picks one of these networks
      //also, add testnet if in demo/staging
      let chains = organization.cryptoPaymentOptions.chains
      const env = keys.environment.NAME

      if (env === 'staging') {
        chains = [...chains, 5]
      }
      setAllowedNetworks(chains)
    }
  }, [organization?.cryptoPaymentOptions])

  //once a network is selected
  useEffect(() => {
    if (selectedNetwork) {
      //set the allowed tokens according to the network selected
      setAllowedTokens(
        findTokensBySymbols(
          selectedNetwork,
          organization.cryptoPaymentOptions.tokens,
        ),
      )
    }
  }, [selectedNetwork])

  useEffect(() => {
    if (selectedNetwork && +selectedNetwork !== walletNetwork) {
      common.errorToast(
        'Must keep wallet network in sync with payment network selected',
      )
      changeNetwork(+selectedNetwork)
    }
  }, [walletNetwork])

  //Web3-Onboard hook to prompt user to connect their wallet. The modal is configured in App.js.
  //the connect function is what displays that modal
  const [{}, connect, disconnect] = useConnectWallet()

  //used by the guest to cancel crypto payment steps
  const cancel = async () => {
    await disconnect({ label: wallet.label })
    dispatch(clearState())
  }

  /**
   * This is intended to compute the total cost of all crypto payments due to Tripsha. I could
   * not get this to compute a valid total cost. So as it stands, each tripsha payment is isolated
   * from all others. Meaning, if someone has $100 USDC in their wallet, but they approve spending
   * for $500 USDC, we would not detect the problem until the charge is attempted. At that point, they
   * should receive an email about failure.
   */
  let totalCost = 0
  if (confirmedTrips && confirmedTrips.length > 0) {
    confirmedTrips.forEach((t) => {
      const { paynowAmount } = getCosting(t)
      totalCost += paynowAmount
    })
  }

  const changeNetwork = async (chainId) => {
    if (!wallet) {
      return
    }

    /**
     * This is the manual way of asking the wallet to switch chains. We need this to make sure
     * any signature includes the correct chain id in the signature parameters.
     */
    try {
      if (chainId !== walletNetwork) {
        const r = await wallet.ethersProvider.send(
          'wallet_switchEthereumChain',
          [{ chainId: `0x${chainId.toString(16)}` }],
        )
        //returns null if switch was successful. Otherwise, an error is thrown.
        if (r) {
          throw r
        }
      }
      //go to next step which is to ask guest for their token preference
      dispatch(setWalletStatus(payState.SELECTING_TOKEN))
    } catch (e) {
      //capture any problem along the way. and bail out.
      if (wallet) {
        disconnect({ label: wallet.label })
      }
      dispatch(clearState())
      dispatch(setWalletError(e.reason || e.message))
    }
  }

  /**
   * This is called when the guest clicks the 'Pay with Crypto' button. Is basically starts the
   * process by asking them to pick a wallet first.
   */
  const startPayment = async (amount, expires) => {
    clearLocal()

    //capture the amount and expiration for the booking
    setUSD(amount)
    setExpiry(expires)

    //clear redux state and set wallet status
    dispatch(clearState())
    dispatch(setWalletStatus(payState.CONNECTING))

    //this waits for user to pick a wallet
    const w = await connect()

    if (w && w.length > 0) {
      //if they picked one, we begin the process of payment
      dispatch(setWalletStatus(payState.CONNECTED))
      const wallet = w[0]
      //setup an ethers provider for doing web3 "stuff"
      wallet.ethersProvider = new ethers.providers.Web3Provider(
        wallet.provider,
        'any',
      )
      wallet.ethersProvider.on('network', async (newNetwork, oldNetwork) => {
        setWalletNetwork(+newNetwork.chainId)
      })

      wallet.address = wallet.accounts[0].address.toLowerCase()
      const net = await wallet.ethersProvider.getNetwork()
      //grab chain id from wallet provider. While web3-onboard supplies a hook for this,
      //this is more accurate to what's actually selected by the guest in their wallet.
      dispatch(setWalletAndChain(w[0], net.chainId))
      setWalletNetwork(net.chainId)

      //force user to select a the network to begin
      dispatch(setWalletStatus(payState.SELECTING_NET))
    }
  }

  /**
   * nextStep is called by the modal when the guest completes selection-based steps
   */
  const nextStep = async (params) => {
    switch (status) {
      //if they were selecting a network
      case payState.SELECTING_NET: {
        let chainId
        if (!params || !params.chainId) {
          chainId = allowedNetworks[0]
        } else {
          chainId = params.chainId
        }
        //grab the network they selected or the default (first). recall this should setup
        //the allowed tokens.
        setSelectedNetwork(chainId)
        /*
                this doesn't work even though all Web3-Onboard claims that it should
                //prompt the user to switch networks. It doesn't and just returns null.
                const r = await setChain({
                    wallet: wallet.label,
                    chainId: chainId.toString()
                });
                */
        await changeNetwork(chainId)

        break
      }

      //if the guest was selecting a token
      case payState.SELECTING_TOKEN: {
        let token = null
        //grab the token they selected or the first one by default
        if (!params || params.token < 0) {
          token = allowedTokens[0]
        } else {
          token = allowedTokens[params.token]
        }
        setSelectedToken(token)

        //do some backend tasks to setup payment info. For starters, we have to
        //conver the USD value into token units now that the guest picked a payment token
        dispatch(setWalletStatus(payState.CONVERTING_CURRENCY))
        let units = undefined
        try {
          units = await PriceConverter.convertToTokenUnits(usd, token)
        } catch (e) {
          dispatch(
            setWalletError(`Problem converting token units ${e.message}`),
          )
          break
        }

        //capture the token units based on price conversion (which uses backend api)
        setTokenUnits(units)

        //next, let's make sure the guest actually has funds to spend in the token they picked
        dispatch(setWalletStatus(payState.CHECKING_BALANCE))

        //see above
        const bal = await checkBalance({
          wallet,
          token: token,
        })

        //if not enough to cover cost of booking
        if (bal < BigInt(units)) {
          //bail out. Modal will display message about insufficient balance. When they hit OK on
          //the modal, see below
          dispatch(
            setWalletError(
              'Insufficient token balance. Please select a different token',
            ),
          )
          dispatch(setWalletStatus(payState.INSUFFICIENT_BALANCE))
          break
        }
        await checkSpend(dispatch, wallet, token, units)
        break
      }

      //if insufficient balance state
      case payState.INSUFFICIENT_BALANCE: {
        //ask the guest to pick a different token
        dispatch(setWalletStatus(payState.SELECTING_TOKEN))
        break
      }

      //if the spend allowance was insufficient
      case payState.SPEND_INSUFFICIENT: {
        //ask the guest to increase spend allowance. See above
        try {
          await applySpend(dispatch, wallet, selectedToken, tokenUnits)
        } catch (e) {
          await disconnect({ label: wallet.label })
          dispatch(clearState())
          dispatch(setWalletError(e.reason || e.message))
        }
        break
      }

      //if sufficient token balance
      case payState.SPEND_SUFFICIENT: {
        try {
          //ask guest to sign a spend allowance
          dispatch(setWalletStatus(payState.SIGNING))
          const { sig, nonce: sigNonce, token: sToken, expiration: sExpiration, amount: sAmount  } = await getSignature({
            wallet,
            token: sToken.address ? sToken.address: sToken,
            amount: sAmount.toString(),
            expiration: sExpiration,
          })

          //as part of the signature, a unique nonce is determined for the guest's allowance.
          //see note above on getSignature about this.
          setSig(sig)
          setNonce(sigNonce)
          setSignedData({
            token: sToken,
            amount: sAmount,
            expiration: sExpiration,
            nonce: sigNonce,
            signature: sig
          })

          //move to next step where trader has signed
          dispatch(setWalletStatus(payState.SIGNED))
        } catch (e) {
          await disconnect({ label: wallet.label })
          dispatch(clearState())
          dispatch(setWalletError(e.reason || e.message))
        }
        break
      }

      //after trader signed
      case payState.SIGNED: {
        //we need to store all the payment details in the backend
        const signed = signedData;
        const extra = {
          nonce: signed.nonce,
          chainId: signed.token.chainId,
          amount: signed.amount.toString(),
          signature: signed.signature,
          token: signed.token.address,
          deadline: signed.expiration,
        }
        /*
        const extra = {
          nonce,
          chainId: selectedNetwork,
          amount: tokenUnits.toString(),
          signature: sig,
          token: selectedToken,
          deadline: expiry,
        }
        */

        try {
          //update the backend
          await updatePaymentRecord(dispatch, cryptoWallet, extra)
          //clear out redux state except payment info
          dispatch(clearState({ leavePayDetails: true }))
          //and disconnect the wallet
          await disconnect({ label: wallet.label })
        } catch (e) {
          dispatch(clearState())
          await disconnect({ label: wallet.label })
          dispatch(setWalletError(e.reason || e.message))
        }
        break
      }
    }
  }

  return {
    startPayment,
    nextStep,
    cancel,
    allowedNetworks,
    allowedTokens,
    selectedNetwork,
    selectedToken,
    walletNetwork,
  }
}
